/**
 * DashboardPage — HeyMotek dashboard home (Phase 3 rebuild, 2026-05-29).
 *
 * Ports the locked preview at /preview/dashboard/home/ in heymotek-web
 * (PLAN_DASHBOARD_REACT_PORT.md). Sections, in order:
 *
 *   1. Greeting + date
 *   2. DashboardBanners (prioritized — WhatsApp > Ad-platform sequential,
 *      Snippet independent)
 *   3. Hero: 30-day contacts count + "X became sales · $Y attributed"
 *   4. Free plan usage bar (Phase 3 /api/usage — limit=100 today;
 *      hides when limit === null for future Pro tier)
 *   5. Per-platform cards: Google Ads / Meta Ads / Organic / Google
 *      Analytics in the 2-col bidirectional/one-way layout
 *   6. Recent activity real <table> (Status last column, top 5 rows)
 *
 * Data sources:
 *   - GET /api/auth/me (via useAuth context) — identity + memberships +
 *     onboarding flags (drives banner block + admin view checks)
 *   - GET /api/stats — totals, by_source per-platform breakdown,
 *     conversions fired counts, recent_contacts (top 5), has_history
 *   - GET /api/usage (Phase 3 new) — Free tier contacts counter
 *
 * Loading: section-level skeletons via Phase 1 Skeleton primitives.
 *   Chrome (greeting, banners) renders immediately.
 *
 * Errors: per-query ErrorState (data-island) so one fetch failure
 *   doesn't blow the whole page.
 *
 * Flagged out-of-scope (NOT fixed in Phase 3):
 *   - Currency: formatCurrency renders each client's currency
 *     (clients.currency via useCurrency), defaulting to USD — never ILS.
 *   - GA card "Events fired" count: not in /api/stats shape today.
 *     Mocked as 0; flagged for backend follow-on.
 *   - Organic card "Conversions fired back" count: same — not exposed.
 *     Mocked as 0; flagged.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { apiRequest } from '@/lib/api';
import { useAuth, useCurrency } from '@/lib/useAuth';
import { STATUS_LABELS, statusBadge } from '@/lib/status';
import type { ContactStatus } from '@/types/contacts';
import { formatCurrency } from '@/lib/format';
import { getSourceLabel, formatLeadDate, platformLogo } from '@/lib/attributionDisplay';
import { DashboardBanners } from '@/components/dashboard/DashboardBanners';
import { PageHeader } from '@/components/PageHeader';
import {
  SkeletonBox,
  SkeletonText,
  SkeletonGroup,
} from '@/components/Skeleton';
import { ErrorState } from '@/components/ErrorState';
import { UsageBar } from '@/components/usage/UsageBar';
import { usageTier } from '@/lib/usage';

// ──────────────────────────────────────────────────────────────────────
// API response types
// ──────────────────────────────────────────────────────────────────────

interface StatsResponse {
  totals: {
    contacts_count: number;
    new_contacts_count: number;
    purchase_count: number;
    attributed_revenue: number;
    conversions_sent_total: number;
    conversions_sent_google: number;
    conversions_sent_facebook: number;
    conversions_sent_google_analytics: number;
  };
  by_source: {
    source: string;
    medium: string | null;
    contacts: number;
    purchase: number;
    revenue: number;
    conversions_sent: number;
  }[];
  recent_contacts: {
    id: string;
    phone: string;
    profile_name: string | null;
    source: string;
    medium: string | null;
    status: string;
    value: number | null;
    created_at: string;
  }[];
  has_history: boolean;
}

interface UsageResponse {
  effective_tier: 'pro' | 'trial' | 'free';
  trial_ends_at: string | null;
  count: number;
  // null = unlimited visibility (trial/pro) — hides the section.
  limit: number | null;
  locked_count: number;
  not_sent_total: number;
  recoverable_count: number;
  period_start: string;
}

// ──────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────

function timeOfDayGreeting(hour: number): string {
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 22) return 'Good evening';
  return 'Good night';
}

function firstNameOf(
  displayName?: string | null,
  email?: string,
): string | null {
  const fromDisplay = displayName?.trim().split(/\s+/)[0];
  if (fromDisplay) return fromDisplay;
  const prefix = email?.split('@')[0]?.split(/[._-]/)[0];
  if (!prefix) return null;
  return prefix.charAt(0).toUpperCase() + prefix.slice(1);
}

// Map a by_source row to one of the four per-platform card slots
// (Google Ads / Meta Ads / Organic / null).  Google Analytics is
// destination-only — not produced by this map.
function classifyBySourceRow(
  source: string,
  medium: string | null,
): 'google-ads' | 'meta-ads' | 'organic' | null {
  const label = getSourceLabel(source, medium);
  if (label === 'Google Ads') return 'google-ads';
  if (label === 'Meta Ads') return 'meta-ads';
  if (
    label === 'Organic' ||
    label === 'Google (organic)' ||
    label === 'Facebook (organic)' ||
    label === 'Bing'
  )
    return 'organic';
  return null;
}

// statusBadge + label live in src/lib/status.ts — single source of truth.
// `recent_contacts[].status` from /api/stats is typed as `string`; we cast
// it to ContactStatus when piping into the helpers since the backend
// guarantees one of the enum values.

// ──────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { me } = useAuth();

  // "New since last visit" anchor: the timestamp of the user's previous dashboard
  // visit, read once on mount (stable for this session). The hero shows how many of
  // the 30-day contacts arrived after it. The stored marker only advances after a
  // ~6h gap, so a refresh / quick re-nav doesn't zero the "+N new" badge mid-session;
  // a genuine new session (e.g. next day) re-anchors it. null = first-ever visit.
  const [lastSeen] = useState(() => localStorage.getItem('hm_dashboard_seen_at'));
  useEffect(() => {
    const SESSION_GAP_MS = 6 * 60 * 60 * 1000;
    const now = Date.now();
    if (!lastSeen || now - new Date(lastSeen).getTime() > SESSION_GAP_MS) {
      localStorage.setItem('hm_dashboard_seen_at', new Date(now).toISOString());
    }
  }, [lastSeen]);

  const statsQuery = useQuery({
    queryKey: ['stats', lastSeen],
    queryFn: () =>
      apiRequest<StatsResponse>(
        lastSeen ? `/stats?since=${encodeURIComponent(lastSeen)}` : '/stats',
      ),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const usageQuery = useQuery({
    queryKey: ['usage'],
    queryFn: () => apiRequest<UsageResponse>('/usage'),
    staleTime: 30_000,
  });

  const greeting = useMemo(() => {
    const now = new Date();
    const band = timeOfDayGreeting(now.getHours());
    const name = firstNameOf(me?.display_name, me?.email);
    const headline = name ? `${band}, ${name}.` : `${band}.`;
    const date = now.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    return { headline, date };
  }, [me?.display_name, me?.email]);

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
      {/* 1. Greeting */}
      <PageHeader title={greeting.headline} subtitle={greeting.date} />

      {/* 2. Banners (prioritized — see DashboardBanners) */}
      <DashboardBanners />

      {/* 3. Hero */}
      <HeroSection statsQuery={statsQuery} />

      {/* 4. Free plan usage — hidden when limit === null (Pro tier) */}
      <UsageSection usageQuery={usageQuery} />

      {/* 5. Per-platform cards */}
      <PlatformsSection statsQuery={statsQuery} me={me} />

      {/* 6. Recent activity */}
      <RecentActivitySection statsQuery={statsQuery} />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Sections
// ──────────────────────────────────────────────────────────────────────

interface StatsQueryProps {
  statsQuery: ReturnType<typeof useQuery<StatsResponse>>;
}

function HeroSection({ statsQuery }: StatsQueryProps) {
  const currency = useCurrency();
  if (statsQuery.isLoading) {
    return (
      <SkeletonGroup className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8 lg:p-10">
        <SkeletonBox className="h-3 w-24" />
        <SkeletonBox tone="prominent" className="mt-3 h-16 w-48 sm:h-20" />
        <SkeletonBox className="mt-3 h-6 w-96 max-w-full" />
        <SkeletonBox className="mt-4 h-4 w-64" />
      </SkeletonGroup>
    );
  }
  if (statsQuery.isError) {
    return (
      <ErrorState
        title="Couldn't load your stats"
        onRetry={() => statsQuery.refetch()}
      />
    );
  }

  const totals = statsQuery.data?.totals;
  const totalContacts = totals?.contacts_count ?? 0;
  const newCount = totals?.new_contacts_count ?? 0;

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-6 shadow-card sm:p-8 lg:p-10">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Last 30 days
        </p>
        {newCount > 0 ? (
          <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-bold text-green-700">
            +{newCount.toLocaleString()} new
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-sm font-bold text-dark">
            <span className="text-green-600">✓</span> You're all caught up
          </span>
        )}
      </div>
      <p className="mt-2 text-5xl font-extrabold leading-none tracking-tight text-dark sm:text-6xl lg:text-7xl">
        {totalContacts.toLocaleString()}
      </p>
      <p className="mt-2 text-lg font-medium text-muted-foreground sm:text-xl">
        {totalContacts === 1 ? 'contact' : 'contacts'} matched to the ads that
        brought them
      </p>
      {totals && totals.purchase_count > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-base">
          <span>
            <span className="font-bold text-dark">{totals.purchase_count}</span>{' '}
            <span className="text-muted-foreground">became sales</span>
          </span>
          <span className="text-dark/20">·</span>
          <span>
            <span className="font-bold text-dark">
              {formatCurrency(totals.attributed_revenue, currency)}
            </span>{' '}
            <span className="text-muted-foreground">attributed</span>
          </span>
        </div>
      )}
    </section>
  );
}

interface UsageQueryProps {
  usageQuery: ReturnType<typeof useQuery<UsageResponse>>;
}

function UsageSection({ usageQuery }: UsageQueryProps) {
  if (usageQuery.isLoading) {
    return (
      <SkeletonGroup className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <SkeletonBox className="h-3 w-28" />
          <SkeletonBox className="h-3 w-36" />
        </div>
        <SkeletonBox className="h-2 w-full rounded-full" />
        <SkeletonBox className="mt-3 h-4 w-72 max-w-full" />
      </SkeletonGroup>
    );
  }

  // Don't render the section on error — it's secondary chrome, not
  // critical. Fail quietly.
  if (usageQuery.isError || !usageQuery.data) return null;

  // Pro tier (when it lands) — limit === null → hide section entirely.
  if (usageQuery.data.limit === null) return null;

  const { count, limit } = usageQuery.data;
  const { state, remaining } = usageTier(count, limit);

  return (
    <section className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Free plan usage
        </span>
        <span className="text-sm font-semibold text-dark">
          {count} / {limit} contacts this month
        </span>
      </div>
      <UsageBar count={count} limit={limit} />
      <p className="mt-3 text-base text-muted-foreground">
        {state === 'over' ? (
          <>
            {usageQuery.data.locked_count} contact
            {usageQuery.data.locked_count === 1 ? ' is' : 's are'} locked this
            month — still captured, nothing lost.{' '}
            <Link to="/account" className="font-semibold text-info hover:underline">
              Upgrade to Pro
            </Link>{' '}
            to see them all.
          </>
        ) : (
          <>
            {remaining} visible contact{remaining === 1 ? '' : 's'} left this
            month.{' '}
            <Link to="/account" className="font-semibold text-info hover:underline">
              Grow into Pro
            </Link>{' '}
            for unlimited.
          </>
        )}
      </p>
      {usageQuery.data.not_sent_total > 0 && (
        <p className="mt-2 text-base text-red-700">
          <span className="font-bold">
            {usageQuery.data.not_sent_total} conversion
            {usageQuery.data.not_sent_total === 1 ? '' : 's'} not sent
          </span>{' '}
          to your ad platforms
          {usageQuery.data.recoverable_count > 0 ? (
            <>
              {' '}
              — {usageQuery.data.recoverable_count} still recoverable if you{' '}
              <Link to="/account" className="font-semibold text-info hover:underline">
                upgrade now
              </Link>
              .
            </>
          ) : (
            '.'
          )}
        </p>
      )}
    </section>
  );
}

interface PlatformsSectionProps extends StatsQueryProps {
  me: ReturnType<typeof useAuth>['me'];
}

function PlatformsSection({ statsQuery, me }: PlatformsSectionProps) {
  // Aggregate by_source into the 4 card slots.
  const platforms = useMemo(() => {
    const acc = {
      'google-ads': { contacts: 0, conversionsSent: 0 },
      'meta-ads': { contacts: 0, conversionsSent: 0 },
      organic: { contacts: 0, conversionsSent: 0 },
    };
    let totalContacts = 0;
    for (const row of statsQuery.data?.by_source ?? []) {
      const slot = classifyBySourceRow(row.source, row.medium);
      if (slot) {
        acc[slot].contacts += row.contacts;
        // ?? 0 guards the brief window where the frontend (CF auto-deploy) is
        // live before the backend deploy adds conversions_sent — avoids NaN.
        acc[slot].conversionsSent += row.conversions_sent ?? 0;
      }
      totalContacts += row.contacts;
    }
    return { ...acc, totalContacts };
  }, [statsQuery.data?.by_source]);

  const totals = statsQuery.data?.totals;
  const pct = (n: number) =>
    platforms.totalContacts > 0
      ? Math.round((n / platforms.totalContacts) * 100)
      : 0;

  // Connected ad platforms drive Organic card's "fired back to" subtitle.
  const connectedAdNames: string[] = [];
  if (me?.google_ads_customer_id) connectedAdNames.push('Google Ads');
  if (me?.facebook_pixel_id) connectedAdNames.push('Meta Ads');
  const organicFiresSubtitle =
    connectedAdNames.length === 0
      ? 'No ad platforms connected'
      : `Last 30 days · to ${connectedAdNames.join(' + ')}`;

  if (statsQuery.isLoading) {
    return (
      <div>
        <SectionLabel>What HeyMotek did this month</SectionLabel>
        <SkeletonGroup className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <PlatformCardSkeleton key={i} />
          ))}
        </SkeletonGroup>
      </div>
    );
  }
  if (statsQuery.isError) {
    return null; // Hero section already shows the error; don't duplicate.
  }

  return (
    <div>
      <SectionLabel>What HeyMotek did this month</SectionLabel>
      <div className="space-y-3">
        <PlatformCard
          name="Google Ads"
          logoSlug="google-ads"
          connected={!!me?.google_ads_customer_id}
          contactsIn={platforms['google-ads'].contacts}
          contactsPct={pct(platforms['google-ads'].contacts)}
          firesOut={totals?.conversions_sent_google ?? 0}
          firesSubtitle="Last 30 days · automatic"
        />
        <PlatformCard
          name="Meta Ads"
          logoSlug="meta"
          connected={!!me?.facebook_pixel_id}
          contactsIn={platforms['meta-ads'].contacts}
          contactsPct={pct(platforms['meta-ads'].contacts)}
          firesOut={totals?.conversions_sent_facebook ?? 0}
          firesSubtitle="Last 30 days · automatic"
        />
        <OrganicCard
          contactsIn={platforms.organic.contacts}
          contactsPct={pct(platforms.organic.contacts)}
          firesOut={
            connectedAdNames.length > 0 ? platforms.organic.conversionsSent : null
          }
          firesSubtitle={organicFiresSubtitle}
        />
        <GoogleAnalyticsCard
          firesOut={totals?.conversions_sent_google_analytics ?? 0}
        />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-3 px-1 text-base font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </h2>
  );
}

interface PlatformCardProps {
  name: string;
  logoSlug: string;
  connected: boolean;
  contactsIn: number;
  contactsPct: number;
  firesOut: number;
  firesSubtitle: string;
}

function PlatformCard({
  name,
  logoSlug,
  connected,
  contactsIn,
  contactsPct,
  firesOut,
  firesSubtitle,
}: PlatformCardProps) {
  return (
    <div className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <img
          src={platformLogo(logoSlug)}
          alt=""
          className="h-8 w-8 shrink-0"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-extrabold text-dark">{name}</h3>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${
            connected
              ? 'bg-green-100 text-green-700'
              : 'bg-dark/5 text-muted-foreground'
          }`}
        >
          {connected ? 'Updated' : 'Not connected'}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Contacts in
          </p>
          <p className="text-3xl font-extrabold tabular-nums leading-none text-dark">
            {contactsIn}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {contactsPct}% of total
          </p>
        </div>
        <div className="sm:border-l sm:border-dark/5 sm:pl-6">
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Conversions fired back
          </p>
          <p className="text-3xl font-extrabold tabular-nums leading-none text-dark">
            {firesOut.toLocaleString()}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{firesSubtitle}</p>
        </div>
      </div>
    </div>
  );
}

interface OrganicCardProps {
  contactsIn: number;
  contactsPct: number;
  firesOut: number | null;
  firesSubtitle: string;
}

function OrganicCard({
  contactsIn,
  contactsPct,
  firesOut,
  firesSubtitle,
}: OrganicCardProps) {
  // firesOut = conversions fired back for organic-source contacts (real count
  // from /api/stats by_source). null → "—" when no ad platform is connected
  // (nothing can fire, so the count isn't meaningful yet).
  return (
    <div className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <img
          src={platformLogo('google-multicolor')}
          alt=""
          className="h-8 w-8 shrink-0"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-extrabold text-dark">Organic</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Search &amp; direct visitors (not from a paid ad)
          </p>
        </div>
        <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-green-700">
          Tracked
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Contacts in
          </p>
          <p className="text-3xl font-extrabold tabular-nums leading-none text-dark">
            {contactsIn}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {contactsPct}% of total
          </p>
        </div>
        <div className="sm:border-l sm:border-dark/5 sm:pl-6">
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Conversions fired back
          </p>
          <p className="text-3xl font-extrabold tabular-nums leading-none text-dark">
            {firesOut === null ? '—' : firesOut.toLocaleString()}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">{firesSubtitle}</p>
        </div>
      </div>
    </div>
  );
}

function GoogleAnalyticsCard({ firesOut }: { firesOut: number }) {
  // Opt-in extra — hidden entirely when GA isn't connected (no "Not connected"
  // for something we supply on the side). Keyed on GA's OWN state (ga_connected),
  // not the legacy google_ads_customer_id mirror.
  const { me } = useAuth();
  if (!me?.ga_connected) return null;
  return (
    <div className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <img
          src={platformLogo('google-analytics')}
          alt=""
          className="h-8 w-8 shrink-0"
          loading="lazy"
        />
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-extrabold text-dark">Google Analytics</h3>
        </div>
        <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-green-700">
          Updated
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <div>
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Sends to GA
          </p>
          <p className="text-base font-bold leading-snug text-dark">
            Conversions + sources
          </p>
        </div>
        <div className="sm:border-l sm:border-dark/5 sm:pl-6">
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Events fired
          </p>
          <p className="text-3xl font-extrabold tabular-nums leading-none text-dark">
            {firesOut.toLocaleString()}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Last 30 days · automatic
          </p>
        </div>
      </div>
    </div>
  );
}

function PlatformCardSkeleton() {
  return (
    <div className="rounded-3xl border border-dark/5 bg-card p-5 shadow-card sm:p-6">
      <div className="mb-4 flex items-center gap-3">
        <SkeletonBox tone="prominent" className="h-8 w-8" />
        <SkeletonBox tone="prominent" className="h-5 w-32 flex-1" />
        <SkeletonBox className="h-5 w-16 rounded-full bg-green-100" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
        <div>
          <SkeletonBox className="mb-2 h-3 w-20" />
          <SkeletonBox tone="prominent" className="h-8 w-16" />
          <SkeletonText className="mt-2 w-24" />
        </div>
        <div className="sm:border-l sm:border-dark/5 sm:pl-6">
          <SkeletonBox className="mb-2 h-3 w-32" />
          <SkeletonBox tone="prominent" className="h-8 w-24" />
          <SkeletonText className="mt-2 w-40" />
        </div>
      </div>
    </div>
  );
}

function RecentActivitySection({ statsQuery }: StatsQueryProps) {
  const currency = useCurrency();
  if (statsQuery.isLoading) {
    return (
      <section>
        <div className="mb-3 flex items-center justify-between px-1">
          <SectionLabel>Recent activity</SectionLabel>
        </div>
        <div className="overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className={`grid grid-cols-4 gap-3 px-6 py-4 ${i > 0 ? 'border-t-2 border-dark/10' : ''}`}
            >
              <SkeletonBox className="h-4 w-16" />
              <SkeletonBox tone="prominent" className="h-4 w-28" />
              <SkeletonBox className="h-4 w-20" />
              <SkeletonBox className="h-4 w-24 justify-self-end" />
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (statsQuery.isError) return null; // hero already showed error

  const recent = statsQuery.data?.recent_contacts ?? [];
  const hasHistory = statsQuery.data?.has_history ?? false;

  if (recent.length === 0) {
    return (
      <section className="rounded-3xl border-2 border-dashed border-dark/15 bg-white p-8 text-center">
        <AlertCircle
          className="mx-auto mb-3 h-10 w-10 text-dark/30"
          strokeWidth={1.5}
        />
        <p className="text-base text-muted-foreground">
          {hasHistory
            ? "Quiet last 30 days. Let's get it moving again."
            : 'Ready when you are. Your first contact will show up here.'}
        </p>
      </section>
    );
  }

  return (
    <section>
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="text-base font-bold uppercase tracking-wider text-muted-foreground">
          Recent activity
        </h2>
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1 text-sm font-semibold text-info hover:underline"
        >
          See all <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <div className="overflow-hidden rounded-3xl border border-dark/5 bg-card shadow-card">
        {/* Desktop table */}
        <table className="hidden w-full border-collapse md:table">
          <tbody>
            {recent.map((row, i) => {
              const badge = statusBadge(row.status as ContactStatus);
              return (
                <tr
                  key={row.id}
                  onClick={() => {
                    window.location.href = '/contacts';
                  }}
                  className={`cursor-pointer transition-colors hover:bg-bg-tint ${i > 0 ? 'border-t-2 border-dark/20' : ''}`}
                >
                  <td className="whitespace-nowrap px-6 py-4 align-middle text-sm text-muted-foreground">
                    {formatLeadDate(row.created_at)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 align-middle font-mono text-base text-dark">
                    {row.phone}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 align-middle text-sm text-muted-foreground">
                    {getSourceLabel(row.source, row.medium)}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right align-middle">
                    <span className="inline-flex items-center gap-2 text-sm">
                      <span className={`h-2 w-2 rounded-full ${badge.dot}`} />
                      <span className={`font-semibold ${badge.text}`}>
                        {STATUS_LABELS[row.status as ContactStatus]}
                      </span>
                      {row.status === 'purchase' && row.value != null && (
                        <span className="font-bold text-dark">
                          · {formatCurrency(row.value, currency)}
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {/* Mobile cards — audit item 3. The desktop table's 4 columns +
            px-6 py-4 cells don't fit on narrow viewports; row text
            overflows and Status gets cut. Mobile gets a card per row
            matching the Contacts mobile pattern. */}
        <div className="md:hidden">
          {recent.map((row, i) => {
            const badge = statusBadge(row.status as ContactStatus);
            return (
              <div
                key={row.id}
                onClick={() => {
                  window.location.href = '/contacts';
                }}
                className={`grid cursor-pointer grid-cols-[1fr_auto] items-center gap-3 px-4 py-3 active:bg-bg-tint ${i > 0 ? 'border-t-2 border-dark/20' : ''}`}
              >
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm text-dark">
                    {row.phone}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {formatLeadDate(row.created_at)} ·{' '}
                    {getSourceLabel(row.source, row.medium)}
                  </p>
                </div>
                <div className="shrink-0">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className={`h-2 w-2 rounded-full ${badge.dot}`} />
                    <span className={`font-semibold ${badge.text}`}>
                      {STATUS_LABELS[row.status as ContactStatus]}
                    </span>
                  </span>
                  {row.status === 'purchase' && row.value != null && (
                    <div className="mt-0.5 text-right text-xs font-bold text-dark">
                      {formatCurrency(row.value, currency)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
